import { useSignalsAccess } from "@/lib/signals/useSignals";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { FEATURES, JobStatus, JobTypes, USER_ROLES } from "@lib/enums";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";
import Quantum from "@components/shared/loading/QuantumLoader";
import { useJobStatus } from "@lib/jobs/useJobStatus";
import useNavigationAccessSnapshot from "./useNavigationAccessSnapshot";

const NavSubheader = styled(ListSubheader)(({ theme }) => ({
  font: "var(--text-label)",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
  padding: "16px 12px 4px",
  backgroundColor: "transparent",
}));

const NavItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  position: "relative",
  padding: "var(--nav-item-padding-y, var(--space-2)) 12px var(--nav-item-padding-y, var(--space-2)) 32px",
  borderBottom: "none",
  backgroundImage: `linear-gradient(${theme.palette.primary.lighter}, ${theme.palette.primary.lighter})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "calc(100% - 24px) 1px",
  backgroundPosition: "12px bottom",
  backgroundColor: active ? theme.palette.primary.lighter : "transparent",
  "& .MuiListItemText-primary": {
    color: active ? theme.palette.primary.main : "inherit",
    fontWeight: active ? "bold" : 400,
  },
  "& .MuiListItemIcon-root": {
    color: active ? theme.palette.primary.main : "var(--text-muted)",
  },
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.lighter
      : theme.palette.action.hover,
  },
}));

const NavTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="right" />
))({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "var(--tooltip-bg)",
    color: "var(--tooltip-text)",
    boxShadow: "var(--tooltip-shadow)",
    borderRadius: "var(--radius-tooltip)",
    padding: "var(--padding-tooltip)",
    font: "var(--text-caption)",
  },
  "& .MuiTooltip-arrow": {
    color: "var(--tooltip-bg)",
  },
});

const NavIcon = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    // Sizes go in style, not width/height attributes: SVG attributes reject var(),
    // and browsers then fall back to the 300x150 default.
    style={{
      width: "var(--nav-icon-size, var(--icon-size-sm))",
      height: "var(--nav-icon-size, var(--icon-size-sm))",
    }}
  >
    {children}
  </svg>
);

export default function Primary({ open }) {
  const pathname = usePathname();
  const { enabled: signalsEnabled } = useSignalsAccess();
  const { recruiter, plan, isLoading } = useRecruiterDetails();
  const { jobs } = useJobStatus(JobTypes.AI, [JobStatus.IMPORTING_CV]);

  const liveIsAdmin =
    recruiter?.role === USER_ROLES.ADMIN ||
    recruiter?.role === USER_ROLES.SUPER_ADMIN ||
    recruiter?.role === USER_ROLES.TEAM_ADMIN;
  const hasPlanFeature = (feature) =>
    plan?.features?.some((item) => item.type === feature) ?? false;
  const liveNavigationAccess =
    !isLoading && recruiter
      ? {
          recruiterName: recruiter.user?.name ?? null,
          submissions: hasPlanFeature(FEATURES.SUBMISSIONS),
          jobs: hasPlanFeature(FEATURES.JOBS),
          reports: liveIsAdmin,
          sola: hasPlanFeature(FEATURES.SOLA),
        }
      : null;
  const navigationAccess = useNavigationAccessSnapshot(liveNavigationAccess);

  const isCVProcessing = jobs.length > 0;

  return (
    <>
      {navigationAccess?.recruiterName && (
        <NavSubheader>{navigationAccess.recruiterName}</NavSubheader>
      )}

      <NavTooltip title="Dashboard" disableHoverListener={open} arrow>
        <NavItem
          component={Link}
          href="/dashboard"
          active={pathname === "/dashboard"}
        >
          <ListItemIcon>
            <NavIcon>
              <rect x="3" y="3" width="8" height="8" rx="1.5"/>
              <rect x="13" y="3" width="8" height="8" rx="1.5"/>
              <rect x="3" y="13" width="8" height="8" rx="1.5"/>
              <rect x="13" y="13" width="8" height="8" rx="1.5"/>
            </NavIcon>
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </NavItem>
      </NavTooltip>

      <NavSubheader>People</NavSubheader>

      <NavTooltip title="Contacts" disableHoverListener={open} arrow>
        <NavItem
          component={Link}
          href="/contacts"
          active={pathname.includes("/contacts")}
        >
          <ListItemIcon>
            <NavIcon>
              <circle cx="8" cy="7" r="3.5"/>
              <path d="M2 21v-1a6 6 0 0112 0v1"/>
              <path d="M16 3.13a4 4 0 010 7.75"/>
              <path d="M22 21v-1a4 4 0 00-3-3.87"/>
            </NavIcon>
          </ListItemIcon>
          <ListItemText primary="Contacts" />
        </NavItem>
      </NavTooltip>

      <NavTooltip title="Candidates" disableHoverListener={open} arrow>
        <NavItem
          component={Link}
          href="/candidates"
          active={pathname.includes("/candidates")}
        >
          <ListItemIcon>
            <NavIcon>
              <circle cx="12" cy="7" r="4"/>
              <path d="M4 21v-1a8 8 0 0116 0v1"/>
            </NavIcon>
          </ListItemIcon>
          <ListItemText primary="Candidates" />
          {isCVProcessing && <Quantum size="20" />}
        </NavItem>
      </NavTooltip>

      <NavSubheader>Outreach</NavSubheader>
      <NavTooltip title="Floats" disableHoverListener={open} arrow>
        <NavItem
          component={Link}
          href="/floats"
          active={pathname.includes("/floats")}
        >
          <ListItemIcon>
            <NavIcon>
              <path d="M4 5h11l5 14H9z"/>
            </NavIcon>
          </ListItemIcon>
          <ListItemText primary="Floats" />
        </NavItem>
      </NavTooltip>

      {navigationAccess?.submissions && (
        <NavTooltip title="Submissions" disableHoverListener={open} arrow>
          <NavItem
            component={Link}
            href="/submissions"
            active={pathname.includes("/submissions")}
          >
            <ListItemIcon>
              <NavIcon>
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </NavIcon>
            </ListItemIcon>
            <ListItemText primary="Submissions" />
          </NavItem>
        </NavTooltip>
      )}

      {navigationAccess?.jobs && (
        <NavTooltip title="Job Ads" disableHoverListener={open} arrow>
          <NavItem
            component={Link}
            href="/jobs"
            active={pathname.includes("/jobs")}
          >
            <ListItemIcon>
              <NavIcon>
                <rect x="3" y="8" width="18" height="13" rx="2"/>
                <path d="M16 8V6a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                <path d="M3 14h18"/>
              </NavIcon>
            </ListItemIcon>
            <ListItemText primary="Job Ads" />
          </NavItem>
        </NavTooltip>
      )}

      <NavSubheader>Insights</NavSubheader>

      {signalsEnabled && (
        <NavTooltip title="Signals" disableHoverListener={open} arrow>
          <NavItem
            component={Link}
            href="/signals"
            active={pathname.startsWith("/signals")}
          >
            <ListItemIcon>
              <NavIcon>
                <circle cx="12" cy="12" r="2"/>
                <path d="M4.93 19.07a10 10 0 010-14.14"/>
                <path d="M7.76 16.24a6 6 0 010-8.49"/>
                <path d="M16.24 7.76a6 6 0 010 8.49"/>
                <path d="M19.07 4.93a10 10 0 010 14.14"/>
              </NavIcon>
            </ListItemIcon>
            <ListItemText primary="Signals" />
          </NavItem>
        </NavTooltip>
      )}

      {navigationAccess?.reports && (
        <NavTooltip title="Reports" disableHoverListener={open} arrow>
          <NavItem
            component={Link}
            href="/reports"
            active={pathname.includes("/reports")}
          >
            <ListItemIcon>
              <NavIcon>
                <polyline points="3 17 8 11 13 14 20 8"/>
                <circle cx="3" cy="17" r="1.5" fill="currentColor" stroke="none"/>
                <circle cx="8" cy="11" r="1.5" fill="currentColor" stroke="none"/>
                <circle cx="13" cy="14" r="1.5" fill="currentColor" stroke="none"/>
                <circle cx="20" cy="8" r="1.5" fill="currentColor" stroke="none"/>
                <path d="M5 3L5.5 5.5L8 6L5.5 6.5L5 9L4.5 6.5L2 6L4.5 5.5Z" fill="currentColor" stroke="none"/>
                <path d="M17 1.5L17.35 3.15L19 3.5L17.35 3.85L17 5.5L16.65 3.85L15 3.5L16.65 3.15Z" fill="currentColor" stroke="none"/>
              </NavIcon>
            </ListItemIcon>
            <ListItemText primary="Reports" />
          </NavItem>
        </NavTooltip>
      )}

      {navigationAccess?.sola && (
        <NavTooltip title="SOLA" disableHoverListener={open} arrow>
          <NavItem
            component={Link}
            href="/sola"
            active={pathname.includes("/sola")}
          >
            <ListItemIcon>
              <Image src="/img/floats/sola.svg" alt="SOLA" width={20} height={20} />
            </ListItemIcon>
            <ListItemText primary="SOLA" />
          </NavItem>
        </NavTooltip>
      )}
    </>
  );
}
